import { ReactElement } from "react"
import { ILogLighting, ISailChange, ISailGroup, ISailName, ISailType } from "../../Types"

export interface LogButtonEngineInfo {
  id: string;
  name: string;
  on: boolean;
  idle?: boolean;
}

// A single selectable navigation-lights option for the lighting quick-record row.
export interface LogButtonLightingOption {
  icon?: ReactElement | string;
  title: string;
  value: ILogLighting;
}

// A single ship sail available to the quick sail-plan toggle. `id` is the
// ShipDataSail id (what the backend records as the status name), `name` is the
// predefined/custom sail name (used for grouping + icon), `sailType` is the
// custom-sail fallback used for grouping, `active` is whether it currently flies.
export interface LogButtonSailInfo {
  id: string;
  name?: string;
  sailType?: string;
  title: string;
  active: boolean;
}

// A rendered sail button: a mutually-exclusive group of candidate sails, or a
// single ungrouped sail (`group` undefined). `activeId` is the staged-active
// member, if any.
export interface LogButtonSailGroupInfo {
  key: string;
  group?: ISailGroup;
  sails: LogButtonSailInfo[];
  activeId?: string;
  activeIds: string[];
}

// Staged sail state: a sparse override map of sail id -> active. A missing entry
// means "unchanged from LogButtonSailInfo.active".
export type LogButtonSailStaged = Record<string, boolean>;

export const LogButtonData = {
  // Whether the engines row should be shown in the dropdown: only when there is
  // no live NMEA engine data, a status callback exists, and at least one engine
  // is configured.
  showEnginesRow(
    hasNmeaEngineData: boolean | undefined,
    engineStatusCallback: ((engineId: string, on: boolean) => void) | undefined,
    engines: LogButtonEngineInfo[] | undefined,
  ): boolean {
    return !hasNmeaEngineData && !!engineStatusCallback && !!engines && engines.length > 0;
  },

  // Whether the lighting row should be shown in the dropdown: only when a capture
  // callback exists and there is at least one selectable lighting option.
  showLightingRow(
    lightingCallback: ((lighting: ILogLighting) => void) | undefined,
    lightingOptions: LogButtonLightingOption[] | undefined,
  ): boolean {
    return !!lightingCallback && !!lightingOptions && lightingOptions.length > 0;
  },

  // Whether the sails block should be shown: only when a save callback exists and
  // the ship has at least one configured sail. Non-sailing vessels pass no sails,
  // so the block never renders for them.
  showSailsRow(
    saveSails: ((changes: ISailChange[]) => void) | undefined,
    sails: LogButtonSailInfo[] | undefined,
  ): boolean {
    return !!saveSails && !!sails && sails.length > 0;
  },

  // The mutually-exclusive group a sail belongs to, mirroring Swift
  // `SailGroup.of(sail:sailType:)`. Resolves first by predefined sail name, then
  // by sail type for custom sails; `undefined` means the sail flies independently.
  sailGroupOf(name?: string, sailType?: string): ISailGroup | undefined {
    if (name && (Object.values(ISailName) as string[]).includes(name)) {
      switch (name as ISailName) {
        case ISailName.mainsail:
        case ISailName.trysail:
          return ISailGroup.main;
        case ISailName.genoa:
        case ISailName.jib:
        case ISailName.workingJib:
        case ISailName.stromJib:
        case ISailName.code0:
          return ISailGroup.foresail;
        case ISailName.spinnaker:
        case ISailName.gennaker:
        case ISailName.parasailor:
          return ISailGroup.spinnaker;
        case ISailName.mizzen:
        case ISailName.mizzenStaysail:
          return ISailGroup.mizzen;
      }
    }
    if (sailType && (Object.values(ISailType) as string[]).includes(sailType)) {
      switch (sailType as ISailType) {
        case ISailType.mast:
          return ISailGroup.main;
        case ISailType.forestay:
          return ISailGroup.foresail;
        case ISailType.mizzen:
          return ISailGroup.mizzen;
      }
    }
    return undefined;
  },

  // Whether a sail is currently staged active (override or, if none, its base state).
  isStaged(staged: LogButtonSailStaged, sail: LogButtonSailInfo): boolean {
    return staged[sail.id] ?? sail.active;
  },

  // Build the per-group render model. Grouped sails collapse into one entry per
  // ISailGroup (declared order); ungrouped sails each become a standalone entry.
  sailGroups(sails: LogButtonSailInfo[], staged: LogButtonSailStaged): LogButtonSailGroupInfo[] {
    const byGroup = new Map<ISailGroup, LogButtonSailInfo[]>();
    const ungrouped: LogButtonSailGroupInfo[] = [];
    for (const s of sails) {
      const g = this.sailGroupOf(s.name, s.sailType);
      if (g === undefined) {
        const active = this.isStaged(staged, s);
        ungrouped.push({ key: `custom-${s.id}`, sails: [s], activeId: active ? s.id : undefined, activeIds: active ? [s.id] : [] });
      } else {
        const arr = byGroup.get(g) ?? [];
        arr.push(s);
        byGroup.set(g, arr);
      }
    }
    const grouped: LogButtonSailGroupInfo[] = [];
    for (const g of Object.values(ISailGroup)) {
      const arr = byGroup.get(g as ISailGroup);
      if (arr && arr.length > 0) {
        const activeIds = arr.filter((s) => this.isStaged(staged, s)).map((s) => s.id);
        grouped.push({ key: g as string, group: g as ISailGroup, sails: arr, activeId: activeIds[0], activeIds });
      }
    }
    return [...grouped, ...ungrouped];
  },

  // Turn every listed sail off in the staged state. Used when a group button with
  // one or more active members is pressed: the first press clears the whole group.
  disableSails(staged: LogButtonSailStaged, sailIds: string[]): LogButtonSailStaged {
    const next = { ...staged };
    for (const id of sailIds) {
      next[id] = false;
    }
    return next;
  },

  // Apply a toggle to the staged state, enforcing group exclusivity: turning a
  // grouped sail on ends every other active sail in the same group.
  toggleSail(sails: LogButtonSailInfo[], staged: LogButtonSailStaged, sailId: string, on: boolean): LogButtonSailStaged {
    const next = { ...staged };
    const target = sails.find((s) => s.id === sailId);
    if (!target) {
      return next;
    }
    if (on) {
      const group = this.sailGroupOf(target.name, target.sailType);
      if (group !== undefined) {
        for (const s of sails) {
          if (s.id !== sailId && this.sailGroupOf(s.name, s.sailType) === group) {
            next[s.id] = false;
          }
        }
      }
      next[sailId] = true;
    } else {
      next[sailId] = false;
    }
    return next;
  },

  // The delta between the current sail plan and the staged one: only sails whose
  // staged active state differs from their base state.
  sailChanges(sails: LogButtonSailInfo[], staged: LogButtonSailStaged): ISailChange[] {
    const changes: ISailChange[] = [];
    for (const s of sails) {
      const now = this.isStaged(staged, s);
      if (now !== s.active) {
        changes.push({ sail: s.id, on: now });
      }
    }
    return changes;
  },
};
